/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  extractServiceErrorBodyMessage,
  extractServiceErrorCode,
  extractServiceErrorMessage
} from "./errorMessage";

const FALLBACK_MESSAGE: string = "An unexpected error occurred";

/**
 * Formats an error caught during a retry attempt into a user-visible
 * overlay message.
 *
 * The text comes from {@link extractServiceErrorMessage}, so every retrying
 * call — TSRI endpoints, msoDefaultDeviceConfig, authConfigurations, claim,
 * bind, domainToken, provisionDevices, deviceFeatures, deviceConfig — reads
 * its reason from the same three places: a mapped error's description, the
 * `{ code, message }` body an axios error carries, then `Error.message`.
 * Reading only the last is why the overlay said "Request failed with status
 * code 401" while the body said "invalid username or password".
 *
 * The code in parentheses is the support handle. It is the service's own code
 * whenever the body supplied the text, since the transport code beside it is
 * only ever the generic `ERR_BAD_REQUEST`.
 */
export function formatRetryErrorMessage(error: unknown): string {
  if (error === null || error === undefined) {
    return FALLBACK_MESSAGE;
  }

  const message: string = extractServiceErrorMessage(error, FALLBACK_MESSAGE);
  if (message === FALLBACK_MESSAGE) {
    return FALLBACK_MESSAGE;
  }

  const errorRecord = error as Record<string, unknown>;
  const transportCode: string | undefined =
    typeof errorRecord.code === "string" && errorRecord.code.length > 0
      ? errorRecord.code
      : undefined;
  // The body's code belongs with the body's text; pairing "invalid username or
  // password" with ERR_BAD_REQUEST names a cause no one can look up.
  const serviceCode: string | undefined =
    extractServiceErrorBodyMessage(error) === message
      ? extractServiceErrorCode(error)
      : undefined;
  const code: string | undefined =
    serviceCode !== undefined ? serviceCode : transportCode;

  if (code !== undefined) {
    return message + " (" + code + ")";
  }
  return message;
}
